import { Stack, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import { useCallback, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { UnsavedChangesConfirmationModal } from '@/components/modals/ConfirmationModal/UnsavedChangesConfirmationModal';
import { useFormContext } from '@/components/react-hook-form';
import { AIOnboardingModalFormShape } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';

type PageChangeDirection = 'next' | 'previous';

function excludeReviewedSuggestionRows(
  formValues: AIOnboardingModalFormShape,
  reviewedSuggestionIDs: string[]
): AIOnboardingModalFormShape {
  const reviewedIDs = new Set(reviewedSuggestionIDs);
  const excludeReviewed = <Row extends { _suggestionID?: string }>(
    rows: Row[]
  ) =>
    rows.filter(
      (row) => !row._suggestionID || !reviewedIDs.has(row._suggestionID)
    );

  return {
    individuals: excludeReviewed(formValues.individuals),
    organizations: excludeReviewed(formValues.organizations),
    entities: excludeReviewed(formValues.entities),
    testamentaryEntities: excludeReviewed(formValues.testamentaryEntities),
    assets: excludeReviewed(formValues.assets),
    liabilities: excludeReviewed(formValues.liabilities),
    lifeInsurancePolicies: excludeReviewed(formValues.lifeInsurancePolicies),
  };
}

export function AIOnboardingModalPagination({
  initialValues,
  disabled = false,
}: {
  initialValues: AIOnboardingModalFormShape;
  disabled?: boolean;
}) {
  const {
    loading,
    suggestionsPagination: {
      currentPage,
      pageSize,
      totalCount,
      currentPageCount,
      reviewedCountBeforeCurrentPage,
      hasNextPage,
      hasPreviousPage,
      onNextPage,
      onPreviousPage,
    },
    reviewedSuggestionIDs,
  } = useAIOnboardingModalContext();
  const {
    formState: { isDirty, isSubmitting },
    getValues,
  } = useFormContext<AIOnboardingModalFormShape>();
  const [pendingPageChange, setPendingPageChange] =
    useState<PageChangeDirection | null>(null);

  const performPageChange = useCallback(
    (direction: PageChangeDirection) => {
      if (direction === 'next') {
        onNextPage();
        return;
      }

      onPreviousPage();
    },
    [onNextPage, onPreviousPage]
  );

  const handlePageChange = useCallback(
    (direction: PageChangeDirection) => {
      const hasUnsavedChanges =
        isDirty &&
        !isEqual(
          excludeReviewedSuggestionRows(getValues(), reviewedSuggestionIDs),
          excludeReviewedSuggestionRows(initialValues, reviewedSuggestionIDs)
        );

      if (hasUnsavedChanges) {
        setPendingPageChange(direction);
        return;
      }

      performPageChange(direction);
    },
    [
      getValues,
      initialValues,
      isDirty,
      performPageChange,
      reviewedSuggestionIDs,
    ]
  );

  const handleUnsavedChangesClose = useCallback(
    (reason: 'confirm' | 'cancel') => {
      if (reason === 'cancel' && pendingPageChange) {
        performPageChange(pendingPageChange);
      }

      setPendingPageChange(null);
    },
    [pendingPageChange, performPageChange]
  );

  if (totalCount <= pageSize && !hasNextPage && !hasPreviousPage) {
    return null;
  }

  const from = currentPage * pageSize - reviewedCountBeforeCurrentPage + 1;
  const to = Math.min(from + currentPageCount - 1, totalCount);
  const hasPageRange = totalCount > 0 && currentPageCount > 0;
  let paginationStatus = 'No remaining suggestions on this page';

  if (hasPageRange) {
    paginationStatus = `Displaying ${from}-${to} of ${totalCount}`;
  }

  return (
    <>
      <Stack
        pt={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body-xs">{paginationStatus}</Typography>
        <Stack spacing={0} direction="row" flexWrap="nowrap">
          {/* Disable while a page fetch is in flight: a second click before
              the new page commits would advance the page counter past the
              recorded cursors and land on the wrong page. */}
          <IconButton
            variant="transparent"
            size="sm"
            icon={ChevronLeftIcon}
            disabled={disabled || !hasPreviousPage || isSubmitting || loading}
            onClick={() => handlePageChange('previous')}
            ariaLabel="Previous page"
          />
          <IconButton
            variant="transparent"
            size="sm"
            icon={ChevronRightIcon}
            disabled={disabled || !hasNextPage || isSubmitting || loading}
            onClick={() => handlePageChange('next')}
            ariaLabel="Next page"
          />
        </Stack>
      </Stack>
      {pendingPageChange && (
        <UnsavedChangesConfirmationModal
          isOpen={true}
          onClose={handleUnsavedChangesClose}
        />
      )}
    </>
  );
}
